import { Link } from "react-router-dom";
import { useMemo } from "react";
import { POLICY_LINKS, POLICY_LINKS_IFT } from "@/data/policies";
import { resolveSiteSlug } from "@/config/siteConfig";

// Site-aware footer content. Each sibling site gets its own logo,
// mission line and Discover/Contribute columns so the footer never
// bleeds Social Pulse marketing onto a child brand's viewport.
// Everything not overridden here falls back to the Social Pulse
// canonical footer.
const SITE_FOOTERS = {
  foodpulse: {
    label:       "Food Pulse™",
    logo:        "/foodpulse-logo-round.png",
    logoAlt:     "Food Pulse™",
    mission:     "Food Pulse™ connects growers, cooks, food banks and neighbours — sharing deals, surplus produce, and everyday recipes to fight food waste and feed communities.",
    discover:    [
      { to: "/food-pulse/articles",   label: "Articles" },
      { to: "/food-pulse/businesses", label: "Food businesses" },
      { to: "/food-pulse/food",       label: "Food encyclopedia" },
      { to: "/recipes",               label: "Recipes" },
    ],
    contribute:  [
      { to: "/recipes/submit",   label: "Share a recipe" },
      { to: "/m/add-deal",       label: "Post a food deal" },
      { to: "/food-pulse/about", label: "About Food Pulse™" },
      { to: "/register",         label: "Create account" },
    ],
    // Site-specific legal links (rendered in the footer's Policies row).
    policies: [
      { slug: "foodpulse-terms-of-service", label: "Terms of Service", to: "/food-pulse/legal/terms" },
      { slug: "foodpulse-privacy-policy",   label: "Privacy Policy" },
      { slug: "foodpulse-cookie-policy",    label: "Cookie Policy" },
      { slug: "childrens-privacy-policy",   label: "Children's Privacy Policy" },
    ],
    parentLine:  "Food Pulse™ is a Division of Social Pulse\u00AE",
    homeUrl:     "https://foodpulse.net",
  },
};

export const Footer = () => {
  // IndianFamilyTree.com surfaces its own Terms of Service in the
  // footer. Every other site continues to see Social Pulse's canonical
  // POLICY_LINKS unchanged.
  const siteSlug = useMemo(() => {
    try {
      // Route-based override — FoodPulse deep links (/food-pulse/*, /basket,
      // /pantry, /receipts, /smart-basket) must render the Food Pulse footer
      // even when served from socialpulse.com (preview or cross-site link).
      if (typeof window !== "undefined") {
        const p = window.location.pathname || "";
        if (/^\/(food-pulse|basket|pantry|receipts|smart-basket)(\/|$)/.test(p)) {
          return "foodpulse";
        }
      }
      const q = typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("site")
        : null;
      return (resolveSiteSlug({ queryParam: q || "" }) || "").toLowerCase();
    } catch { return ""; }
  }, []);
  const isIft = siteSlug.includes("indianfamilytree");
  const policyLinks = isIft ? POLICY_LINKS_IFT : POLICY_LINKS;
  // Default to FoodPulse brand on all pages — unless a specific site overrides it.
  const brand = SITE_FOOTERS[siteSlug] || SITE_FOOTERS.foodpulse;
  // Brand can override the policy link list; falls back to the site-wide POLICY_LINKS.
  const brandPolicyLinks = brand?.policies || policyLinks;
  if (brand) {
    // ── Branded footer (Food Pulse on all pages, IFT on IFT routes, etc.) ──
    return (
      <footer data-testid="site-footer" data-site={siteSlug} className="mt-24 border-t" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <Link to="/" data-testid="footer-logo-link" aria-label={`${brand.label} home`}>
                <img src={brand.logo} alt={brand.logoAlt} className="h-12 w-auto object-contain rounded-md" />
              </Link>
            </div>
            <p className="max-w-md text-sm leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
              {brand.mission}
            </p>
          </div>
          <div>
            <div className="overline mb-4">Discover</div>
            <ul className="space-y-2 text-sm">
              {brand.discover.map((l) => (
                <li key={l.to}><Link to={l.to}>{l.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="overline mb-4">Contribute</div>
            <ul className="space-y-2 text-sm">
              {brand.contribute.map((l) => (
                <li key={l.to}><Link to={l.to}>{l.label}</Link></li>
              ))}
            </ul>
          </div>
        </div>

        {/* Policies row */}
        <div className="border-t" style={{ borderColor: "var(--wofy-border)" }}>
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6" data-testid="footer-policies">
            <div className="overline mb-3">Policies</div>
            <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
              {brandPolicyLinks.map((p) => (
                <li key={p.slug}>
                  <Link
                    to={p.to || `/policies/${p.slug}`}
                    data-testid={`footer-policy-${p.slug}`}
                    className="hover:underline"
                  >
                    {p.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t" style={{ borderColor: "var(--wofy-border)" }}>
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs font-mono tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
            <span data-testid="footer-copyright">
              © {new Date().getFullYear()}{" "}
              <a href={brand.homeUrl} className="underline hover:no-underline" style={{ color: "var(--wofy-primary)" }}>
                {brand.label}
              </a>
              <span className="ml-2" style={{ opacity: 0.75 }} data-testid="footer-parent-line">
                — {brand.parentLine}
              </span>
            </span>
            <span>ONE BILLION ACTS OF KINDNESS.</span>
          </div>
        </div>
      </footer>
    );
  }
  return (
  <footer data-testid="site-footer" className="mt-24 border-t" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-4 gap-10">
      <div className="md:col-span-2">
        <div className="flex items-center gap-3 mb-4">
          <Link to="/" data-testid="footer-logo-link" aria-label="Social Pulse home">
            <img src="/assets/social-pulse-logo.jpg" alt="Social Pulse" className="h-12 w-auto object-contain rounded-md" />
          </Link>
        </div>
        <p className="max-w-md text-sm leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
          The greatest joy in life comes from helping others. Social Pulse connects those who want to help with those who genuinely need it — regardless of religion, nationality, race, gender or affiliation.
        </p>
      </div>

      <div>
        <div className="overline mb-4">Discover</div>
        <ul className="space-y-2 text-sm">
          <li><Link to="/find-help">Find Help</Link></li>
          <li><Link to="/organizations">Organizations</Link></li>
          <li><Link to="/housing">Housing Projects</Link></li>
          <li><Link to="/opportunities">Volunteer</Link></li>
        </ul>
      </div>

      <div>
        <div className="overline mb-4">Contribute</div>
        <ul className="space-y-2 text-sm">
          <li><Link to="/offer-help">Offer Help</Link></li>
          <li><Link to="/stories">Success Stories</Link></li>
          <li><Link to="/dashboard">My Dashboard</Link></li>
          <li><Link to="/register">Create account</Link></li>
        </ul>
      </div>
    </div>

    {/* Policies row — visible on every page per requirement */}
    <div className="border-t" style={{ borderColor: "var(--wofy-border)" }}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6" data-testid="footer-policies">
        <div className="overline mb-3">Policies</div>
        <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          {policyLinks.map((p) => (
            <li key={p.slug}>
              <Link
                to={`/policies/${p.slug}`}
                data-testid={`footer-policy-${p.slug}`}
                className="hover:underline"
              >
                {p.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>

    <div className="border-t" style={{ borderColor: "var(--wofy-border)" }}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs font-mono tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
        <span data-testid="footer-copyright">
          © {new Date().getFullYear()}{" "}
          <a
            href="https://socialpulse.com"
            className="underline hover:no-underline"
            style={{ color: "var(--wofy-primary)" }}
          >
            Social Pulse Inc
          </a>
        </span>
        <span>ONE BILLION ACTS OF KINDNESS.</span>
      </div>
    </div>
  </footer>
  );
};
